import type { StepKind } from '@sb/routine-runner';

/**
 * The step kinds that accept a `Waypoint` child, named one at a time.
 *
 * Deliberately not derived from "is this a weld step?". Holding an arc and
 * holding waypoints are separate properties: a future weld step whose motion is
 * fully described by its own arguments — a seam traced from a scan, say — would
 * take no waypoints at all, and inheriting permission from the family name is
 * exactly how it would silently acquire them. Adding a step here is a decision,
 * not a consequence.
 *
 * Shared because two places have to make the same call and disagreeing is worse
 * than either answer: the validation rule (`rules/steps/Waypoint.ts`) and the
 * wizard gate that decides whether a selected waypoint can render at all
 * (`WaypointStepView`). The wrist teach button reads it too, since a taught
 * position becomes a waypoint child of whatever is selected.
 */
export const WAYPOINT_PARENT_STEP_KINDS: readonly StepKind[] = [
  'MoveArmToV2',
  'Weld',
  'RotaryWeld',
];

export function permitsWaypointChild(kind: string | null | undefined): boolean {
  return (
    kind != null &&
    (WAYPOINT_PARENT_STEP_KINDS as readonly string[]).includes(kind)
  );
}

/**
 * Of those, the ones that hold exactly one waypoint rather than a sequence.
 *
 * A separate list again: taking waypoints and taking more than one are
 * different properties. It exists because a teach has to know which it is —
 * appending to a single-waypoint parent produces a routine its own rule
 * rejects, so the wrist button overwrites there instead of adding.
 */
export const SINGLE_WAYPOINT_PARENT_STEP_KINDS: readonly StepKind[] = [
  'RotaryWeld',
];

export function holdsSingleWaypoint(kind: string | null | undefined): boolean {
  return (
    kind != null &&
    (SINGLE_WAYPOINT_PARENT_STEP_KINDS as readonly string[]).includes(kind)
  );
}
